import { Printer, FileDown, Bluetooth, Wifi } from "lucide-react";
import { toast } from "sonner";
import { usePrintBridge } from "@/hooks/usePrintBridge";

/**
 * One-click LAN thermal print button with a visible PDF-download companion.
 *
 * Props:
 *   tspl              – TSPL endpoint URL used by "Print to LAN Printer"
 *   pdf               – PDF endpoint URL. Renders "PDF" download button.
 *                       Also opens automatically if the LAN relay is offline.
 *   label             – short human name (e.g. "RM labels") for toasts
 *   disabled          – disable both buttons
 *   variant           – "primary" (dark) | "outline" (default) | "icon" (icons only)
 *   size              – "sm" | "md" (default "sm")
 *   className         – extra classes on the wrapping group
 *   testId            – data-testid for the main Print button
 *   showWireless      – render the placeholder "Print to Wireless Printer"
 *                       button (default false; enable on tablet-facing pages)
 *   children          – custom label text; defaults to "Print to LAN Printer"
 */
const PrintButton = ({
  tspl,
  pdf = null,
  label = "labels",
  disabled = false,
  variant = "outline",
  size = "sm",
  className = "",
  testId = "print-btn",
  showWireless = false,
  children,
}) => {
  const { bridgeAlive, print } = usePrintBridge();

  const onPrint = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!tspl || disabled) return;
    // Relay POSTs the TSPL to 127.0.0.1:9911, which forwards it over TCP to
    // the LAN printer at 192.168.1.100:9100. If the relay is offline, the
    // usePrintBridge helper opens the PDF fallback automatically.
    print(tspl, { label, pdfFallbackUrl: pdf });
  };

  const onPdf = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!pdf || disabled) return;
    window.open(pdf, "_blank", "noreferrer");
    toast.success(`Opening ${label} PDF`, { duration: 2000 });
  };

  const onWireless = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toast.info("Wireless (Bluetooth) printing is coming soon — waiting on hardware.", { duration: 3500 });
  };

  // --- Icon-only variant (Inventory row action) ---
  if (variant === "icon") {
    return (
      <span className={`inline-flex items-center gap-0.5 ${className}`}>
        <button
          type="button"
          onClick={onPrint}
          disabled={disabled || !tspl}
          data-testid={testId}
          aria-label={`Print ${label} to LAN printer`}
          title={
            bridgeAlive === true ? `One-click LAN print · ${label}` :
            bridgeAlive === false ? `LAN relay offline · opens PDF · ${label}` :
            `Print ${label}`
          }
          className={`p-1.5 transition-colors ${
            bridgeAlive === true ? "text-emerald-700 hover:text-emerald-900" : "text-slate-400 hover:text-blue-700"
          }`}
        >
          <Printer className="h-4 w-4" />
        </button>
        {pdf && (
          <button
            type="button"
            onClick={onPdf}
            disabled={disabled}
            data-testid={`${testId}-pdf`}
            aria-label={`Download ${label} PDF`}
            title={`Download ${label} PDF`}
            className="p-1.5 text-slate-400 hover:text-slate-900 transition-colors"
          >
            <FileDown className="h-4 w-4" />
          </button>
        )}
      </span>
    );
  }

  const sizeCls = size === "md" ? "h-9 px-3 text-sm" : "h-8 px-2.5 text-xs";
  const iconSize = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
  const variantCls =
    variant === "primary"
      ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-900"
      : bridgeAlive === true
      ? "bg-blue-600 hover:bg-blue-700 border-blue-700 text-white"
      : "bg-white hover:bg-slate-50 border-slate-300 text-slate-800";

  return (
    <span className={`inline-flex items-center gap-1 flex-wrap ${className}`}>
      <button
        type="button"
        onClick={onPrint}
        disabled={disabled || !tspl}
        data-testid={testId}
        title={
          bridgeAlive === true ? `One-click LAN print · ${label}` :
          bridgeAlive === false ? `LAN relay offline · will open PDF · ${label}` :
          `Print ${label} to LAN printer`
        }
        className={`inline-flex items-center gap-1.5 font-semibold border rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sizeCls} ${variantCls}`}
      >
        <Wifi className={iconSize} />
        {children ?? "Print to LAN Printer"}
      </button>
      {pdf && (
        <button
          type="button"
          onClick={onPdf}
          disabled={disabled}
          data-testid={`${testId}-pdf`}
          title={`Download ${label} as PDF`}
          className={`inline-flex items-center gap-1.5 font-semibold border rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sizeCls} bg-white hover:bg-slate-50 border-slate-300 text-slate-700`}
        >
          <FileDown className={iconSize} />
          PDF
        </button>
      )}
      {showWireless && (
        /*  Tablet-friendly: 44 px tall (Apple HIG minimum touch target), 12 px
            horizontal padding, big pill radius, tap-highlight enabled.  */
        <button
          type="button"
          onClick={onWireless}
          disabled={disabled}
          data-testid={`${testId}-wireless`}
          aria-label={`Print ${label} via wireless printer`}
          title="Wireless (Bluetooth) printer — coming soon"
          className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-4 rounded-full text-sm font-semibold border-2 bg-white hover:bg-emerald-50 active:bg-emerald-100 border-emerald-500 text-emerald-800 transition-colors disabled:opacity-50 select-none"
          style={{ WebkitTapHighlightColor: "rgba(16,185,129,0.2)" }}
        >
          <Bluetooth className="h-4 w-4" />
          Print to Wireless Printer
        </button>
      )}
    </span>
  );
};

export default PrintButton;
